/**
 * checkoutAckWatcher.js
 *
 * Watches for SHOW_CHECKOUT_FORM acknowledgment from the checkout iframe after
 * a DONATE postMessage is sent. If the ack never arrives within the timeout,
 * fires a Sentry warning — indicating the checkout iframe failed to render.
 *
 * It also listens for CHECKOUT_PRELOAD_LOST from the embed script on the host
 * page (CON-86 / PE-2140) and captures it to PostHog. That script runs on the
 * nonprofit's own page and has no analytics client of its own, so this iframe is
 * where a lost Donate tap becomes countable.
 *
 * Why here and not in checkout:
 * If checkout never mounts, its code never runs. The watchdog must live in the
 * sender (donation-elements / hosted-page), which is always running.
 *
 * Usage:
 *   const cancelWatcher = watchForCheckoutAck({ context: { sender, formId, amount, frequency } });
 *   // Call cancelWatcher() on component unmount to prevent stale alerts.
 */

import { Sentry, markCheckoutTapLost } from '@charitystack/shared';

const TIMEOUT_MS = 12000;

/**
 * Start watching for the SHOW_CHECKOUT_FORM ack.
 *
 * @param {object} options
 * @param {number} [options.timeoutMs=12000] - ms to wait before firing Sentry alert
 * @param {object} [options.context] - diagnostic context included in the Sentry event
 * @param {Function} [options.acceptAck] - optional predicate that narrows messages that cancel the alarm
 * @param {string} [options.context.sender]    - form component name (e.g. 'STANDARD_FORM')
 * @param {string} [options.context.formId]    - elementID / formID of the form
 * @param {number} [options.context.amount]    - donation amount
 * @param {string} [options.context.frequency] - donation frequency
 * @returns {Function} cancel — call this on unmount or successful ack to clean up
 */
export function watchForCheckoutAck({ timeoutMs = TIMEOUT_MS, context = {}, acceptAck } = {}) {
  let timeoutId = null;

  const onMessage = (event) => {
    if (!event.data || event.data.entity !== 'CHARITYSTACK') return;

    if (event.data.action === 'SHOW_CHECKOUT_FORM') {
      if (acceptAck) {
        try {
          if (!acceptAck(event)) return;
        } catch {
          return;
        }
      }
      // Checkout rendered and acknowledged the DONATE message — all good.
      cancel();
      return;
    }

    // CON-86 / PE-2140. This watcher deliberately does NOT capture the event —
    // `ensureLostCheckoutTapListener` below is the single capture site, and it
    // is armed by every form including the one the hosted page renders inline in
    // its own document. Capturing here as well would double-count the hosted
    // layout, where both listeners sit on the same window.
    //
    // What this DOES own is standing its own alarm down. 'navigated' is
    // terminal: the donor is being sent to the hosted donation page, so this
    // document is about to unload and the 12 s Sentry warning would otherwise
    // fire on the way out, reporting a render failure for a donor who was
    // successfully rerouted. The recoverable reasons deliberately do not cancel
    // — if the rebuilt frame never acks either, that IS a render failure and
    // Sentry should still hear about it.
    // Authenticated for the same reason the capture above is, and with more at
    // stake: this SILENCES the render-failure alarm, so an unauthenticated
    // sender could hide a broken checkout from Sentry entirely.
    if (event.data.action !== 'CHECKOUT_PRELOAD_LOST' || event.data.reason !== 'navigated') {
      return;
    }

    if (event.source !== window.parent && event.source !== window) return;

    if (acceptAck) {
      try {
        if (!acceptAck(event)) return;
      } catch {
        return;
      }
    }

    cancel();
  };

  const cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    window.removeEventListener('message', onMessage);
  };

  window.addEventListener('message', onMessage);

  timeoutId = setTimeout(() => {
    window.removeEventListener('message', onMessage);
    timeoutId = null;

    Sentry.captureMessage(
      'donation-elements: No SHOW_CHECKOUT_FORM ack received within 12s after DONATE',
      {
        level: 'warning',
        tags: {
          sender: context.sender || 'unknown',
          form_id: context.formId || 'unknown',
          no_ack_reason: 'checkout_render_failure',
        },
        contexts: {
          checkout_ack_diagnostic: {
            sender: context.sender || 'unknown',
            form_id: context.formId || 'unknown',
            amount: context.amount,
            frequency: context.frequency,
            url: window.location.href,
            referrer: document.referrer,
          },
        },
      },
    );
  }, timeoutMs);

  return cancel;
}

/**
 * CON-86 / PE-2140 — capture a lost Donate tap inside a FORM iframe.
 *
 * This is the SINGLE capture site for the event, and it is a singleton listener
 * per document. `watchForCheckoutAck` above deliberately does not capture, only
 * cancels its own alarm — on a hosted page the form is rendered inline in the
 * hosted document rather than in an iframe, so both listeners would be on the
 * same window and a single incident would be counted twice.
 *
 * It is armed from the form components themselves, which cover both layouts:
 * embedded on a nonprofit's site the form is an iframe and the marker is posted
 * to it; on a hosted page the same components render inline and the marker is
 * posted to the hosted document. Either way the listener is there.
 *
 * What this deliberately does NOT do is arm the 12 s Sentry watchdog. The
 * SHOW_CHECKOUT_FORM ack is sent to the hosted script, not to the form iframe,
 * so a watchdog armed in here would never see its ack and would report a render
 * failure on every single successful embedded donation.
 *
 * Idempotent: safe to call on every DONATE. At most one listener per document,
 * and it is never removed — it is one closure for the life of the page.
 */
let lostTapListenerInstalled = false;
/**
 * The context of the most recent DONATE, not the first.
 *
 * The listener is installed once, but a page can carry several forms, so it
 * cannot close over whichever one happened to arm it first — the tap that gets
 * lost belongs to the form that armed it LAST.
 */
let latestLostTapContext = {};

export function ensureLostCheckoutTapListener(context = {}) {
  if (typeof window === 'undefined') return;
  latestLostTapContext = context;

  if (lostTapListenerInstalled) return;
  lostTapListenerInstalled = true;

  window.addEventListener('message', (event) => {
    if (
      !event.data ||
      event.data.entity !== 'CHARITYSTACK' ||
      event.data.action !== 'CHECKOUT_PRELOAD_LOST'
    ) {
      return;
    }

    // Only the host page sends this. Embedded, that is our parent; on a hosted
    // page the form is inline, so it is this window. Without the check, any
    // frame or injected script on a nonprofit's page could inflate the count.
    if (event.source !== window.parent && event.source !== window) return;

    markCheckoutTapLost(event.data.reason, {
      elapsed_ms: event.data.elapsedMs,
      sender: latestLostTapContext.sender || 'unknown',
    });
  });
}

/** Test seam — the listener is a module-level singleton. */
export function resetLostCheckoutTapListenerForTests() {
  lostTapListenerInstalled = false;
}
